import { Box, Button, IconButton, Typography } from '@mui/material';
import FileSaver from 'file-saver';
import moment from 'moment';
import React, { FC, RefObject, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AcceptIcon from '../../../assets/icons/accept-icon';
import CancelIcon from '../../../assets/icons/cancel-icon';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { AppState } from '../../../store';
import { RemoteFlashActionNames } from '../../../store/configure/remote-flash/types';
import { setModalOpen } from '../../../store/modal/actions';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import {
  associateCommunicatorFileSubscription,
  removeSelectedRows,
  uploadSoftwareFiles,
} from '../../../store/software-library-management/actions';
import {
  LibraryManagementActionNames,
  SoftwareFilesRecord,
} from '../../../store/software-library-management/types';
import { VirtualTableHeadCell } from '../../virtualized-table/types';
import Table from '../../bulk-device-management/table';
import FileUploader from '../../file-loader';
import { communicatorFilesHeadCells, communicatorFilesSortableAccessors, uploadCommunicatorFilesHeaderCells, uploadCommunicatorFilesSortableAccessors } from './head-cells';
import useStyles from './styles';
import { RetrieveConfigFilesLibrary } from '../../../entities/config-files-library-v1/retrieve-config-files-library';

const setPercentUpload = () => {};

const ConfigFilesSection: FC<{}> = () => {
  const [columnVisibility, setColumnVisibility] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const tableContainer: RefObject<HTMLDivElement> = useRef(null);
  const uploadTableContainer: RefObject<HTMLDivElement> = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const tableData = useSelector((state: AppState) => state.libraryManagement.communicatorFiles);
  const [configFile, setConfigFile] = useState<any>([]);
  const [uploadConfig, setUploadConfig] = useState<Boolean>(false);
  const [communicatorFile, setCommunicatorFile] = useState<any>();
  const [uploadCommunicator, setUploadCommunicator] = useState<Boolean>(false);
  const [uploadTableData, setUploadTableData] = useState<any[]>([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const fileReader = new FileReader();

  const TableData = {
    title: 'Communicator Files',
    storageString: 'library-management-communicator-files',
    headerColumns: communicatorFilesHeadCells,
    sortableAccessors: communicatorFilesSortableAccessors,
    onRowSelection: setSelectedRows,
    data: tableData,
    id: 'communicator-files',
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    tableContainer,
  };

  const UploadTableData = {
    title: 'Upload Communicator',
    storageString: 'upload-communicator-files',
    headerColumns: uploadCommunicatorFilesHeaderCells,
    sortableAccessors: uploadCommunicatorFilesSortableAccessors,
    data: uploadTableData,
    id: 'upload-communicator-files',
    setColumnVisibility: () =>{},
    columnVisibility: [],
    setColumnOrder: () => {},
    columnOrder: [],
    tableContainer,
  }

  const handleDeleteSelected = () => {
    dispatch(
      removeSelectedRows(tableData, selectedRows, (deletedIds: Array<string>) => {
        const filteredRows = tableData.filter((row) => !deletedIds.includes(row.id));
        dispatch({
          type: LibraryManagementActionNames.SET_COMMUNICATOR_FILES,
          payload: filteredRows,
        });
      }),
    );
  };

  const clearUploadConfig = () => {
    setUploadConfig(false);
    setConfigFile([]);
  };
  const clearUploadCommunicator = () => {
    setUploadCommunicator(false);
    setCommunicatorFile(null);
  };

  const setTableData = useCallback(
    (data: any) => {
      dispatch({
        type: LibraryManagementActionNames.SET_COMMUNICATOR_FILES,
        payload: data,
      });
    },
    [dispatch],
  );

  const getUploadConfig = (fileObject: File, resolve: Function) => ({
    file: fileObject,
    fileTypeId: fileObject.name.endsWith('.xml') ? '10086' : '10516',
    partNumber: '',
    overwrite: false,
    isRetreivable: true,
    setPercentUpload,
    finishedCallback: (response: SoftwareFilesRecord) => {
      resolve({response, success: true});
      dispatch(
        enqueueSnackbarAction({
          message: 'Communicator config file upload successful',
          options: {
            variant: 'success',
          },
        }),
      );
    },
    overwriteCallback: () => {
      resolve({response: fileObject, success: false})
    }
  })

  const acceptUploadConfig = async () => {
    setUploadTableData(uploadTableData.map((data:any) => ({...data, status: 'Uploading...'})));
    const newUploadData = [...uploadTableData];
    const responses: any[] = await Promise.all(configFile.map(
      (configFileObject: File) =>
        new Promise((resolve) => {
          dispatch(uploadSoftwareFiles(getUploadConfig(configFileObject, resolve)));
        }).then((result: any) => {
          const existing = newUploadData.findIndex(item => item.file === configFileObject);
          if(existing > -1) {
            newUploadData[existing] = {
              ...newUploadData[existing],
              status: result.success ? 'Upload successful' : 'Upload failed'
            }
          }
          return result;
        })
      )
    );
    setUploadTableData(newUploadData);
    const successData: any[] = [];
    /* eslint-disable no-restricted-syntax */
    for(const item of responses) {
      if(item.success) {
        successData.push(item.response);
      } else {
        /* eslint-disable no-await-in-loop */
        const overwrite: any = await new Promise(resolve => dispatch(setModalOpen({
          open: true,
          message: `A file with identical contents named ${item.response.name} already exists in the database, would you like to overwrite the file?`,
          accept: () => dispatch(uploadSoftwareFiles({    
            ...getUploadConfig(item.response, resolve), 
            overwrite: true
          })),
          cancel: () => {
            resolve({success: false});
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            })
          }
        })));
        if(overwrite.success) {
          successData.push(overwrite.response);
        }
      }
   }
    setTableData([...successData, ...tableData.filter(data => !successData.some(newData => newData.id === data.id))]);
    clearUploadConfig();
  };

  const acceptUploadCommunicator = async () => {
    if (communicatorFile.type.endsWith("text/csv")) {
      fileReader.readAsText(communicatorFile);
    } else {
      dispatch(
      enqueueSnackbarAction({
        message: 'Only csv file should be allowed',
        options: {
          key: (Date.now() + Math.random()).toString(),
          variant: 'error'
        },
      }));
    }
  };

  const associateCommunicatorFile = async (
    subscriptionId: string,
    hash: string,
  ) => {
    const payload = {
      associatedSchedulerConfigs: [
        {
          schedulerConfigs: [
            {
              hash
            },
          ],
        },
      ],
    };
    return dispatch(associateCommunicatorFileSubscription(subscriptionId, payload));
  };

  const communicatorUploadFinished = async (
    fileText: any,
    response: SoftwareFilesRecord,
  ) => {
    const arrayResults = fileText.split(/\r?\n/);
    if (arrayResults.length > 0) {
      for (const arrayResult of arrayResults) {
        const [subscriptionId, hash] = arrayResult.split(',');
        if (subscriptionId && hash) {
          await associateCommunicatorFile(subscriptionId, hash);
        }
      }

      setTableData([response, ...tableData.filter(data => data.id !== response.id)]);
      dispatch(
        enqueueSnackbarAction({
          message: 'Associate communicator config file upload successful',
          options: {
            variant: 'success',
          },
        }),
      );
    }
  };

  const handleCommunicatorFile = (files: File[]) => {
    setConfigFile([
      ...configFile,
      ...[...files].filter((file:File) => !configFile.some((config:File) => config.name === file.name))
    ]);
  }

  const handleOnLoad = (event: any) => {
    const fileText = event.target.result;
    const uploadConfig = {
      file: communicatorFile,
      fileTypeId: '10064',
      partNumber: '',
      overwrite: false,
      isRetrievable: true,
      setPercentUpload,
      finishedCallback: (response: any) => communicatorUploadFinished(fileText, response),
      overwriteCallback: () => dispatch(setModalOpen({
        open: true,
        message: `A file with identical contents named ${communicatorFile.name} already exists in the database, would you like to overwrite the file?`,
        accept: () => dispatch(uploadSoftwareFiles({
          ...uploadConfig,
          overwrite: true
        })),
        cancel: () => {
          dispatch({
            type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
            payload: false,
          })
        }
      }))
    };
    dispatch(uploadSoftwareFiles(uploadConfig));
    clearUploadCommunicator();
  };

  fileReader.onloadend = handleOnLoad;

  const getConfigurationFiles = useCallback(async () => {
    const configFilesLibrary = new ConfigurationFilesLibrary(
      client,
      envVariables.configurationFilesLibraryV1BaseUrl,
    );

    const responses = await Promise.all(['10086','10516','10064'].map(fileTypeId => configFilesLibrary.getConfigFiles({fileTypeId, isRetrievable: true})));
    const configFilesLibraries = responses
    .reduce((result, response) => [
        ...result,
        ...response.data.configs || []
      ], [] as RetrieveConfigFilesLibrary[])
    .sort((a,b) => (new Date(b.createdTime|| 'now').getTime()) - (new Date(a.createdTime || 'now').getTime()));
    if (configFilesLibraries) { 
      setTableData(
        configFilesLibraries.map((file) => ({
          ...file,
          userId: file.lastUpdatedByUserId,
        })),
      );
    } else {
      setTableData(null);
    }
  }, [client, envVariables.configurationFilesLibraryV1BaseUrl, setTableData]);

  useEffect(() => {
    getConfigurationFiles();
  }, [getConfigurationFiles]);

  const handleExport = () => {
    const columnsToExport = communicatorFilesHeadCells.filter(header => header.accessor !== 'downloadIcon');
    try {
      const headerString = `${columnsToExport.map(
        (headerCell: VirtualTableHeadCell) => `${headerCell.Header}`,
      )}\n`;
      const rowAsString = tableData.map(
        (row: any) =>
          `${columnsToExport.map(
            (headerCell: VirtualTableHeadCell) => `${row[headerCell.accessor] || ''}`,
          )}\n`,
      );
      const blob = new Blob([`${headerString}${rowAsString.join('')}`], {
        type: 'text/plain;charset=utf-8',
      });
      FileSaver.saveAs(blob, `Export_${moment().format('MMMM_Do_YYYY_h:mm:ss_a')}.csv`)
      dispatch(
        enqueueSnackbarAction({
          message: 'Export successful.',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );
    } catch {
      dispatch(
        enqueueSnackbarAction({
          message: 'Export failed.',
          options: { 
            key: (Date.now() + Math.random()).toString(),
            variant: 'error' 
          },
        }),
      );
    }
  }

  useEffect(() => {
    setUploadTableData(configFile.map((file: File) => {
      const existing = uploadTableData.find(item => item === file);
      return {
          file,
          fileName: file.name,
          status: existing ? existing.status : 'Waiting to be uploaded',
          actions: {
            remove: () => setConfigFile(configFile.filter((item: File) => item !== file))
          },
        }
      }))
  }, [configFile]);

  return (
    <div className={classes.scrollBox}>
      <Box mb={3}>
        <Typography variant="h6">Upload Communicator Config File</Typography>
      </Box>
      <div className={classes.actionRow}>
        <FileUploader
          text="Choose File(s)"
          buttonId="config-files-upload"
          handleFile={(files: any[]) => handleCommunicatorFile(files)}
          multiple
          accept='.xml,.pb'
        />
        <Button
          className={classes.button}
          variant="outlined"
          size="small"
          onClick={() => setUploadConfig(true)}
          disabled={configFile.length === 0}
        >
          Upload File(s)
        </Button>
        {configFile.length > 0 && uploadConfig && (
          <Box className={classes.confirm} display="flex" alignItems="center">
            <Typography>Are you sure?</Typography>
            <IconButton
              id="config-files-confirm"
              onClick={acceptUploadConfig}
              aria-label="accept"
              size="large">
              <AcceptIcon />
            </IconButton>
            <IconButton
              id="config-files-deny"
              onClick={clearUploadConfig}
              aria-label="cancel"
              size="large">
              <CancelIcon />
            </IconButton>
          </Box>
        )}
      </div>
      {configFile.length > 0 && (
        <Box mt={1}>
          <div ref={uploadTableContainer} className={classes.tableContainer}>
            <Table {...UploadTableData} />
          </div>
        </Box>
      )}
      <Box mt={3} mb={3}>
        <Typography variant="h6">
          Associate Communicator Configuration File to Subscription
        </Typography>
      </Box>
      <div className={classes.actionRow}>
        <FileUploader
          text="Choose File"
          buttonId="communicator-files-upload"
          handleFile={setCommunicatorFile}
          accept=".csv"
        />
        <Button
          className={classes.button}
          variant="outlined"
          size="small"
          onClick={() => setUploadCommunicator(true)}
          disabled={!communicatorFile}
        >
          Upload File
        </Button>
        {communicatorFile && uploadCommunicator && (
          <Box className={classes.confirm} display="flex" alignItems="center">
            <Typography>Are you sure?</Typography>
            <IconButton
              id="config-files-confirm"
              onClick={acceptUploadCommunicator}
              aria-label="accept"
              size="large">
              <AcceptIcon />
            </IconButton>
            <IconButton
              id="config-files-deny"
              onClick={clearUploadCommunicator}
              aria-label="cancel"
              size="large">
              <CancelIcon />
            </IconButton>
          </Box>
        )}
      </div>
      {communicatorFile && <Box mt={1}>{communicatorFile.name}</Box>}
      <Box mt={3} mb={3}>
        <Typography variant="h6">Communicator Config Repository</Typography>
      </Box>
      <div className={classes.actionRow}>
        <Button
          className={classes.button}
          variant="outlined"
          size="small"
          onClick={handleDeleteSelected}
          disabled={selectedRows.length === 0}
        >
          Delete Selected
        </Button>
        <Button
        className={classes.button}
        variant="outlined"
        size="small"
        onClick={handleExport}
        >
          Export All
        </Button>
      </div>
      <div ref={tableContainer} className={classes.tableContainer}>
        <Table {...TableData} />
      </div>
    </div>
  );
};

export default ConfigFilesSection;
